import React, { useState, useEffect, useCallback } from 'react';
import { LocalUser, RoleDefinition } from '../../types';
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  resetPassword,
  getRoles,
} from '../../services/systemApi';

const UserManagementTab: React.FC = () => {
  const [users, setUsers] = useState<LocalUser[]>([]);
  const [roles, setRoles] = useState<Record<string, RoleDefinition>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit state
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editRole, setEditRole] = useState('');

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Reset password state
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);

  const roleNames = Object.keys(roles).sort();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load users and roles independently so one failure doesn't block the other
      const [usersResult, rolesResult] = await Promise.allSettled([getUsers(), getRoles()]);
      
      if (usersResult.status === 'fulfilled') {
        setUsers(usersResult.value);
      } else {
        setError(usersResult.reason?.message || 'Unable to load users.');
      }
      
      if (rolesResult.status === 'fulfilled') {
        setRoles(rolesResult.value);
      } else {
        // Roles failing is less critical — dropdown will just be empty
        console.error('[UserManagement] Failed to load roles:', rolesResult.reason);
      }
    } catch (err: any) {
      setError(err.message || 'Unable to load users. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleCreate = async () => {
    clearMessages();
    setCreateError(null);

    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      setCreateError('Email is required');
      return;
    }
    if (!newPassword) {
      setCreateError('Temporary password is required');
      return;
    }
    if (!newRole) {
      setCreateError('Role is required');
      return;
    }

    setSaving(true);
    try {
      await createUser(trimmedEmail, newPassword, newRole);
      setSuccess(`User "${trimmedEmail}" created successfully.`);
      setShowCreateForm(false);
      setNewEmail('');
      setNewPassword('');
      setNewRole('');
      setCreateError(null);
      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Failed to create user.';
      setCreateError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (user: LocalUser) => {
    clearMessages();
    setEditingEmail(user.email);
    setEditRole(user.roleName);
  };

  const handleEditSave = async () => {
    if (!editingEmail) return;
    if (!editRole) {
      setError('Please select a role before saving.');
      return;
    }
    clearMessages();
    setSaving(true);
    try {
      await updateUser(editingEmail, editRole);
      setSuccess(`User "${editingEmail}" role updated successfully.`);
      setEditingEmail(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update user role.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingEmail(null);
    setEditRole('');
  };

  const handleDeleteClick = (email: string) => {
    clearMessages();
    setDeleteConfirm(email);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    clearMessages();
    setSaving(true);
    try {
      await deleteUser(deleteConfirm);
      setSuccess(`User "${deleteConfirm}" deleted successfully.`);
      setDeleteConfirm(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete user.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetClick = (email: string) => {
    clearMessages();
    setResetEmail(email);
    setResetNewPassword('');
    setResetError(null);
  };

  const handleResetConfirm = async () => {
    if (!resetEmail) return;
    if (!resetNewPassword) {
      setResetError('Temporary password is required');
      return;
    }
    clearMessages();
    setSaving(true);
    try {
      await resetPassword(resetEmail, resetNewPassword);
      setSuccess(`Password reset for "${resetEmail}" successfully.`);
      setResetEmail(null);
      setResetNewPassword('');
      setResetError(null);
    } catch (err: any) {
      setResetError(err?.response?.data?.message || err.message || 'Failed to reset password.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewEmail('');
    setNewPassword('');
    setNewRole('');
    setCreateError(null);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  if (loading && users.length === 0) {
    return <div className="text-gray-500">Loading users...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
          <span>{error}</span>
          <button
            onClick={() => { setError(null); loadData(); }}
            className="ml-4 text-sm font-medium text-red-700 underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Success banner */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center justify-between" role="status">
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="ml-4 text-sm font-medium text-green-700 hover:text-green-900"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header with Add New button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Users</h3>
        {!showCreateForm && (
          <button
            type="button"
            onClick={() => { clearMessages(); setShowCreateForm(true); }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add New
          </button>
        )}
      </div>

      {/* Create user form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Create New User</h4>
          <div className="space-y-4">
            <div>
              <label htmlFor="user-email" className="block text-sm font-medium text-gray-700">Email</label>
              <input
                id="user-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className={`mt-1 block w-full max-w-md rounded-md shadow-sm sm:text-sm ${
                  createError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label htmlFor="user-password" className="block text-sm font-medium text-gray-700">Temporary Password</label>
              <input
                id="user-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full max-w-md rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Temporary password"
              />
            </div>
            <div>
              <label htmlFor="user-role" className="block text-sm font-medium text-gray-700">Role</label>
              <select
                id="user-role"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="mt-1 block w-full max-w-md rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a role</option>
                {roleNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            {createError && <p className="text-sm text-red-600">{createError}</p>}
          </div>
          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancelCreate}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Reset password dialog */}
      {resetEmail && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            Reset Password for {resetEmail}
          </h4>
          <div className="space-y-4">
            <div>
              <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700">New Temporary Password</label>
              <input
                id="reset-password"
                type="password"
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                className="mt-1 block w-full max-w-md rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="New temporary password"
              />
              {resetError && <p className="mt-1 text-sm text-red-600">{resetError}</p>}
            </div>
          </div>
          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleResetConfirm}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
            <button
              type="button"
              onClick={() => { setResetEmail(null); setResetError(null); }}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Users table */}
      {users.length === 0 && !showCreateForm ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No users found. Click "Add New" to create one.
        </div>
      ) : (
        users.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <React.Fragment key={user.email}>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 'CONFIRMED'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {editingEmail === user.email ? (
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                            className="rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                            aria-label={`Role for ${user.email}`}
                          >
                            <option value="">Select a role</option>
                            {roleNames.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        ) : (
                          user.roleName
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.created)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                        {editingEmail === user.email ? (
                          <>
                            <button
                              type="button"
                              onClick={handleEditSave}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={handleEditCancel}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleEditClick(user)}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetClick(user.email)}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                              Reset Password
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(user.email)}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {/* Delete confirmation */}
                    {deleteConfirm === user.email && (
                      <tr>
                        <td colSpan={5} className="px-6 py-3">
                          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md" role="alert">
                            <p className="text-sm">
                              Are you sure you want to delete user <span className="font-medium">{user.email}</span>?
                              This action cannot be undone.
                            </p>
                            <div className="mt-2 flex space-x-3">
                              <button
                                type="button"
                                onClick={handleDeleteConfirm}
                                disabled={saving}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                              >
                                {saving ? 'Deleting...' : 'Confirm Delete'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default UserManagementTab;
